<template>
  <ModalDialog v-model="showModal" :closeOnEsc="false" :showXCloseButton="true" size="md">
    <template v-if="showModal">
      <div v-if="modalState == ModalState.CanOrder">
        <RichText :field="orderNewCardTextContent" />
        <div class="flex flex-col gap-4 mt-8">
          <button class="btn btn-primary btn-md" @click="orderNewPhysicalCard">
            {{ orderNewCardBtn }}
          </button>
          <button class="btn btn-secondary btn-md" @click="onCancelOrderClick">Cancel</button>
        </div>
      </div>
      <div v-if="modalState == ModalState.AlreadyOrdered">
        <AlreadyOrdered
          :membershipCardModal="membershipCardModal"
          @closeChildModal="onCloseChildModal"
        ></AlreadyOrdered>
      </div>
      <div v-if="modalState == ModalState.SuccessfullyOrdered">
        <SuccessfullyOrdered
          :membershipCardModal="membershipCardModal"
          @closeChildModal="onCloseChildModal"
        ></SuccessfullyOrdered>
      </div>
      <div v-if="modalState == ModalState.Error">
        <OrderError :membershipCardModal="membershipCardModal" @closeChildModal="onCloseChildModal"></OrderError>
      </div>
    </template>
    <template v-else><div></div></template>
  </ModalDialog>
</template>
<script lang="ts" setup>
import {
  SettingCategory,
  useAccountStore,
  useConfigSettingsStore,
  useSelfServicePhysicalMembershipCardStore,
  useAnalyticsStore,
} from '@/store';
import ModalDialog from '../ModalDialog/ModalDialog.vue';
import { ref, computed, watch } from 'vue';
import { addDays, isBefore } from 'date-fns';
import { RichText } from '@sitecore-jss/sitecore-jss-vue';
import { replaceBookmark } from '@/utils/bookmark.replace';
import SuccessfullyOrdered from './SuccessfullyOrdered.vue';
import AlreadyOrdered from './AlreadyOrdered.vue';
import OrderError from './OrderError.vue';
import { FormEvents } from '@/enums/form-events.enum';

const accountStore = useAccountStore();
const configStore = useConfigSettingsStore();
const physicalCardStore = useSelfServicePhysicalMembershipCardStore();
const analyticsStore = useAnalyticsStore();

const showModal = ref<boolean>(false);
const loading = ref<boolean>(false);

enum ModalState {
  CanOrder,
  AlreadyOrdered,
  SuccessfullyOrdered,
  Error,
}

const modalState = ref<ModalState>(ModalState.CanOrder);

const props = defineProps<{
  membershipCardModal: any;
}>();

watch(
  () => showModal.value,
  (newVal) => {
    if (newVal) {
      const lastRequestDate = accountStore.identityDetails?.membershipCardRequestedDate;
      if (lastRequestDate) {
        physicalCardStore.calcExpectedDeliveryDate(lastRequestDate);
        const canOrderNewCard = !lastRequestDate || isBefore(physicalCardStore.expectedArrivalDate!, new Date());
        if (!canOrderNewCard) {
          modalState.value = ModalState.AlreadyOrdered;
        }
      }
    }
  }
);

const orderNewCardBtn = computed(() => {
  return props.membershipCardModal?.fields?.orderNewCardBtn?.value;
});

const orderNewCardTextContent = computed(() => {
  const orderNewCardText = props.membershipCardModal?.fields?.orderNewCardText?.value;

  let value = replaceBookmark('firstname', orderNewCardText ?? '', accountStore.identityDetails?.firstName ?? '');
  value = replaceBookmark('lastname', value, accountStore.identityDetails?.lastName ?? '');
  const postalAddress = accountStore.identityDetails?.addresses?.find(
    (item) => item.addressType?.toLowerCase() == 'postal'
  );

  if (postalAddress) {
    value = replaceBookmark('addressLine1', value, postalAddress.formattedLong ?? '');
  }

  return { value };
});

defineExpose({ showModal: () => (showModal.value = true) });

const calculateWaitTime = async () => {
  if (!physicalCardStore.waitTime) {
    const waitTime = (await configStore.getSetting(
      'WaitTimeForPhysicalCard',
      SettingCategory.CommonSettings
    )) as number;
    physicalCardStore.waitTime = waitTime ?? 30;
  }

  const lastRequestDate = accountStore.identityDetails?.membershipCardRequestedDate;
  physicalCardStore.calcExpectedDeliveryDate(lastRequestDate);
  const canOrderNewCard = !lastRequestDate || isBefore(physicalCardStore.expectedArrivalDate!, new Date());

  if (canOrderNewCard) {
    modalState.value = ModalState.CanOrder;
    analyticsStore.pushAnalytics({
      event: FormEvents.START,
      form: {
        name: 'Order physical card',
        stepName: 'FormStarted',
      },
    });
  } else {
    modalState.value = ModalState.AlreadyOrdered;
    analyticsStore.pushAnalytics({
      event: FormEvents.JEOPARDY,
      form: {
        name: 'Order physical card',
        stepName: 'FormStarted',
        formErrorDetail: 'You have already ordered a physical card',
        formErrorType: 'We found an existing request',
      },
    });
  }
};

watch(showModal, calculateWaitTime);

const orderNewPhysicalCard = async () => {
  loading.value = true;
  try {
    const res = await physicalCardStore.orderCard();
    if (!res?.value) throw res;

    accountStore.identityDetails!.membershipCardRequestedDate = new Date();
    calculateWaitTime();
    modalState.value = ModalState.SuccessfullyOrdered;
    analyticsStore.pushAnalytics({
      event: FormEvents.COMPLETE,
      form: {
        name: 'Order physical card',
        stepName: 'FormCompleted',
      },
    });
  } catch (error) {
    modalState.value = ModalState.Error;
    analyticsStore.pushAnalytics({
      event: FormEvents.JEOPARDY,
      form: {
        name: 'Order physical card',
        formErrorDetail: 'We are having trouble processing your request',
        formErrorType: 'Unable to complete order',
      },
    });
    console.error('Unable to order physical card: ', error);
  }
  loading.value = false;
};

const onCancelOrderClick = () => {
  showModal.value = false;
};

const onCloseChildModal = async () => {
  showModal.value = false;
};
</script>
